import { useCallback, useRef, useState } from 'react';
import { useCreateConversation } from '../api/use-create-conversation';
import {
  ensureAssistantHandoffConversation,
  readAssistantHandoff,
} from './pending-handoff';

/** All attachments and the first send share the same conversation creation. */
export function useChatConversation(
  initialId?: string,
  onCreated?: (id: string) => void,
  handoffToken?: string
) {
  const { createConversationAsync } = useCreateConversation();
  const handoffId = readAssistantHandoff(handoffToken)?.conversationId;
  const [conversationId, setConversationId] = useState(initialId ?? handoffId);
  const idRef = useRef(initialId ?? handoffId);
  const pendingRef = useRef<Promise<string> | null>(null);

  const ensureConversation = useCallback(
    (title?: string): Promise<string> => {
      if (idRef.current) return Promise.resolve(idRef.current);
      if (pendingRef.current) return pendingRef.current;
      const create = () =>
        createConversationAsync(title ? { title } : {}).then(({ id }) => id);
      const pending = (
        handoffToken
          ? ensureAssistantHandoffConversation(handoffToken, create)
          : create()
      )
        .then((id) => {
          idRef.current = id;
          setConversationId(id);
          onCreated?.(id);
          return id;
        })
        .finally(() => {
          pendingRef.current = null;
        });
      pendingRef.current = pending;
      return pending;
    },
    [createConversationAsync, onCreated, handoffToken]
  );

  return { conversationId, ensureConversation };
}
